<template>
    <v-card flat color="transparent" class="w-100 align-self-start">
        <v-card-title>
            <span class="secondary--text">Instances</span>
        </v-card-title>
        <v-card-text>
            <div v-if="!fetchingInstances" class="d-flex flex-wrap align-center">
                <add-instance-dialog v-if="spaceInstances.length">
                    <v-btn color="primary">
                        <v-icon small left>mdi-plus</v-icon>
                        <span>Instance</span>
                    </v-btn>
                </add-instance-dialog>
                <v-spacer />
                <v-text-field
                    class="mx-2"
                    v-show="spaceInstances.length"
                    placeholder="Search for an instance..."
                    autofocus
                    prepend-inner-icon="mdi-magnify "
                    v-model="instanceSearch"
                    solo
                    flat
                    hide-details
                    dense
                    clearable>
                    <template v-slot:append>
                        <v-menu offset-y :close-on-content-click="false">
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn v-bind="attrs" v-on="on" small class="mr-n2">
                                    <v-icon left>mdi-tune-variant</v-icon>
                                    <span>Filters</span>
                                </v-btn>
                            </template>
                            <v-list nav dense>
                                <div v-for="(item, index) in Object.entries(userCheckboxBinders)" :key="index">
                                    <v-sheet rounded class="py-2 px-4 my-1">
                                        <v-switch inset dense hide-details class="pa-0 ma-0" v-model="item[1].status">
                                            <template v-slot:label>
                                                <div class="d-flex align-center">
                                                    <v-icon left small>{{ item[1].icon }}</v-icon>
                                                    <span class="text-capitalize">{{ item[0] }}</span>
                                                </div>
                                            </template>
                                        </v-switch>
                                    </v-sheet>
                                </div>
                            </v-list>
                        </v-menu>
                    </template>
                </v-text-field>
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <v-btn v-on="on" @click="$store.dispatch('spaceStore/fetchSpaceInstances', $route.params.sid)" text icon>
                            <v-icon>mdi-refresh</v-icon>
                        </v-btn>
                    </template>
                    <span>refresh</span>
                </v-tooltip>
            </div>

            <div v-if="!fetchingInstances">
                <v-data-table
                    v-if="spaceInstances.length"
                    :headers="instanceHeaders"
                    :items="filteredInstancesByRole"
                    item-key="iid"
                    :items-per-page.sync="itemsPerPage"
                    :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
                    class="mt-4">
                    <template v-slot:top="{ options, pagination, updateOptions }">
                        <div class="d-flex align-center">
                            <v-spacer></v-spacer>
                            <v-data-footer
                                :pagination="pagination"
                                :options="options"
                                @update:options="updateOptions"
                                :itemsPerPageOptions="itemsPerPageOptions"
                                items-per-page-text="$vuetify.dataTable.itemsPerPageText"
                                class="no-border" />
                        </div>
                    </template>
                    <template v-slot:[`item.type`]="{ item }">
                        <v-icon>{{ getInstanceTypeIcon(item) }}</v-icon>
                    </template>
                    <template v-slot:[`item.instance`]="{ item }">
                        <div class="d-flex flex-column py-2">
                            <router-link
                                :to="{
                                    name: 'snapshot-overview',
                                    params: { oid: $route.params.oid, sid: $route.params.sid, iid: item.iid, snid: item.dev_snid }
                                }"
                                style="text-decoration: none">
                                <v-hover v-slot:default="{ hover }">
                                    <span :class="[hover ? 'secondary--text' : 'secondary--text', 'font-weight-medium']">
                                        {{ item.long_id }}
                                    </span>
                                </v-hover>
                            </router-link>
                            <span class="caption mr-3">{{ item.description }}</span>
                        </div>
                    </template>
                    <template v-slot:[`item.roles`]="{ item }">
                        <v-chip-group column>
                            <template v-if="spaceAdmins.length && userCheckboxBinders.administrators.status">
                                <v-chip outlined label small v-for="admin in spaceAdmins" :key="`${item.iid}-admin-${admin.ucid}`">
                                    <v-icon small left>mdi-shield-account-outline</v-icon>
                                    {{ admin.full_name }}
                                </v-chip>
                            </template>
                            <template v-if="instanceRolesPerInstance(item.long_id).length">
                                <v-chip
                                    outlined
                                    label
                                    small
                                    v-for="(role, index) in instanceRolesPerInstance(item.long_id)"
                                    :key="`${item.iid}-${role.ucid}-${index}`">
                                    <v-icon small left>{{ getRoleIcon(role.role_name) }}</v-icon>
                                    {{ role.full_name }}
                                </v-chip>
                            </template>
                        </v-chip-group>
                        <SpaceInviteDialog :data-for-instance-to-invite-to="{ instanceName: item.long_id }">
                            <template v-slot:activator>
                                <v-btn small text block class="secondary--text justify-start">
                                    <v-icon small color="primary">mdi-plus</v-icon>
                                    Users
                                </v-btn>
                            </template>
                        </SpaceInviteDialog>
                    </template>
                    <template v-slot:[`item.actions`]="{ item }">
                        <v-menu left offset-x nudge-top="10">
                            <template v-slot:activator="{ on }">
                                <v-btn v-on="on" text color="secondary" small>
                                    <v-icon>more_horiz</v-icon>
                                </v-btn>
                            </template>
                            <v-list nav dense>
                                <instance-edit-dialog :instance="item">
                                    <template v-slot:activator="{ attrs, disabled, open }">
                                        <v-list-item v-bind="attrs" :disabled="disabled" @click.stop="open">
                                            <v-list-item-icon>
                                                <v-icon :color="disabled ? null : 'secondary'" :class="disabled ? 'text--disabled' : ''">
                                                    mdi-pencil-outline
                                                </v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-title :class="[disabled ? 'text--disabled' : 'secondary--text', 'mr-5']">Edit</v-list-item-title>
                                        </v-list-item>
                                    </template>
                                </instance-edit-dialog>
                                <v-divider class="my-2"></v-divider>
                                <instance-delete-dialog
                                    :instanceId="parseInt(item.iid, 10)"
                                    :instanceName="item.long_id"
                                    :masterInstanceId="masterInstance.iid.toString()"
                                    :masterDevelopmentSnapshotId="masterInstance.dev_snid.toString()">
                                    <template v-slot:activator="{ attrs, disabled, open }">
                                        <v-list-item v-bind="attrs" :disabled="disabled" @click.stop="open">
                                            <v-list-item-icon>
                                                <v-icon :color="disabled ? null : 'error'" :class="disabled ? 'text--disabled' : ''">
                                                    mdi-trash-can-outline
                                                </v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-title :class="[disabled ? 'text--disabled' : 'error--text', 'mr-5']">Delete</v-list-item-title>
                                        </v-list-item>
                                    </template>
                                </instance-delete-dialog>
                            </v-list>
                        </v-menu>
                    </template>
                    <template v-slot:no-data>
                        <span v-if="instanceSearch">No instances found.</span>
                        <span v-else>No instances found. Consider adding a new instance above.</span>
                    </template>
                </v-data-table>

                <div v-else>
                    <v-banner class="mb-5" two-line>
                        <v-avatar slot="icon" color="white" size="60"><v-icon x-large color="info">mdi-information-outline</v-icon></v-avatar>
                        <div class="d-flex flex-column">
                            <span class="secondary--text font-weight-bold mb-1">No instances found</span>
                        </div>
                        <template v-slot:actions>
                            <add-instance-dialog>
                                <v-btn color="primary">
                                    <v-icon small>mdi-plus</v-icon>
                                    <span>Instance</span>
                                </v-btn>
                            </add-instance-dialog>
                        </template>
                    </v-banner>
                </div>
            </div>

            <div v-else>
                <v-skeleton-loader class="mx-auto" type="table"></v-skeleton-loader>
            </div>
        </v-card-text>
    </v-card>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { enumsData } from '@/mixins/enums'
import { getInstanceTypeIcon, instanceRole } from '@/utils'
const InstanceDeleteDialog = () => import('@/modules/instance/components/TheInstanceDeleteDialog.vue')
const InstanceEditDialog = () => import('@/modules/instance/components/InstanceEditDialog.vue')
const AddInstanceDialog = () => import('@/modules/space/components/TheSpaceAddInstanceDialog.vue')
const SpaceInviteDialog = () => import('@/modules/space/components/SpaceInviteDialog.vue')

export default {
    name: 'InstanceList',
    mixins: [enumsData],
    components: { InstanceDeleteDialog, AddInstanceDialog, InstanceEditDialog, SpaceInviteDialog },
    data() {
        return {
            instanceSearch: '',
            itemsPerPage: 10,
            itemsPerPageOptions: [10, 25, 50, -1],
            userCheckboxBinders: {
                administrators: { status: true, icon: 'mdi-shield-account-outline' },
                editors: { status: true, icon: instanceRole('editor').icon },
                viewers: { status: true, icon: instanceRole('viewer').icon },
                observers: { status: true, icon: instanceRole('observer').icon }
            }
        }
    },
    methods: {
        instanceRolesPerInstance: function (instanceName) {
            return this.instanceRoles.filter(instance => instance.Instance === instanceName)
        },
        getRoleIcon(roleName) {
            return instanceRole(roleName).icon
        },
        getInstanceTypeIcon(instance) {
            return getInstanceTypeIcon(instance?.long_id)
        }
    },
    mounted() {
        this.$store.dispatch('spaceStore/fetchSpaceUsers', this.$route.params.sid)
    },
    computed: {
        ...mapState('spaceStore', ['spaceInstances', 'spaceUsers', 'fetchingInstances']),
        ...mapGetters('spaceStore', ['currentSpaceType']),
        instanceHeaders() {
            return [
                { text: 'Type', value: 'type', sortable: false, width: '40px' },
                { text: 'Instance', value: 'instance', sortable: false, width: '360px' },
                { text: 'Roles', value: 'roles', sortable: false },
                { text: 'Actions', value: 'actions', align: 'right', sortable: false, width: '120px' }
            ]
        },
        instanceRoles() {
            if (this.spaceUsers) {
                return this.spaceUsers
                    .filter(user => user.instance_viewer_roles.length || user.instance_editor_roles.length || user.instance_observer_roles.length)
                    .map(user => {
                        let finalData = []
                        if (this.userCheckboxBinders.editors.status) {
                            const userEditorData = user.instance_editor_roles.map(instance =>
                                Object.assign({}, instance, { ucid: user.ucid, uid: user.uid, full_name: user.full_name, email: user.email })
                            )
                            finalData = finalData.concat(userEditorData)
                        }
                        if (this.userCheckboxBinders.viewers.status) {
                            const userViewerData = user.instance_viewer_roles.map(instance =>
                                Object.assign({}, instance, { ucid: user.ucid, uid: user.uid, full_name: user.full_name, email: user.email })
                            )
                            finalData = finalData.concat(userViewerData)
                        }
                        if (this.userCheckboxBinders.observers.status) {
                            const userObserverData = user.instance_observer_roles.map(instance =>
                                Object.assign({}, instance, { ucid: user.ucid, uid: user.uid, full_name: user.full_name, email: user.email })
                            )
                            finalData = finalData.concat(userObserverData)
                        }
                        return finalData
                    })
                    .reduce((a, b) => [...a, ...b], [])
            }
            return []
        },
        filteredSpaceInstances() {
            if (!this.instanceSearch) return this.spaceInstances
            const keyword = this.instanceSearch.toLowerCase()
            return this.spaceInstances.filter(instance => {
                const name = (instance.long_id || '').toLowerCase()
                const description = (instance.description || '').toLowerCase()
                return name.includes(keyword) || description.includes(keyword)
            })
        },
        filteredInstancesByRole() {
            return this.filteredSpaceInstances.filter(instance => {
                const roles = this.instanceRolesPerInstance(instance.long_id)
                const hasAdmin = this.spaceAdmins.length > 0
                const hasEditor = roles.some(role => role.role_name === this.roleTypes.INSTANCE_EDITOR)
                const hasViewer = roles.some(role => role.role_name === this.roleTypes.INSTANCE_VIEWER)
                const hasObserver = roles.some(role => role.role_name === this.roleTypes.INSTANCE_OBSERVER)

                return (
                    (this.userCheckboxBinders.administrators.status && hasAdmin) ||
                    (this.userCheckboxBinders.editors.status && hasEditor) ||
                    (this.userCheckboxBinders.viewers.status && hasViewer) ||
                    (this.userCheckboxBinders.observers.status && hasObserver)
                )
            })
        },
        spaceAdmins() {
            return this.spaceUsers.filter(user => user.space_role === this.roleTypes.SPACE_ADMIN)
        },
        masterInstance() {
            const masterInstance = this.spaceInstances.find(instance => instance.long_id === this.instanceFixedNames.MASTER_INSTANCE)
            return masterInstance
        }
    }
}
</script>

<style scoped lang="scss">
.activeClass::before {
    opacity: 0;
}
</style>
